import { DatePipe } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { DeployChannelType } from '@core/models/deploy-plan-config.model';
import { isNil } from '@core/utils/shared-utils';

import {
  ModalAction,
  ModalComponent,
  ModalConfig,
  ModalSize,
  ModalType
} from '@shared/components/modal/modal.component';

/**
 * Last look before a deploy. Deploying is irreversible in every channel — an e-mail cannot be
 * recalled, a CSV cannot be un-uploaded, and a GedMouv tour cannot be deleted from here at all
 * — so the confirmation is unconditional rather than reserved for the re-send case.
 *
 * The re-send warning is what the operator actually needs to read: it names the messages that
 * have already gone out and will go out again.
 */
@Component({
  selector: 'app-deploy-confirm-modal',
  imports: [ModalComponent, TranslatePipe, DatePipe],
  templateUrl: './deploy-confirm-modal.component.html',
  styles: ``
})
export class DeployConfirmModalComponent {
  private readonly translate = inject(TranslateService);

  readonly channelName = input.required<string>();
  readonly channel = input.required<DeployChannelType>();
  /**
   * How many messages the request carries, or null for a channel that deploys as one body.
   * Drives which of the two questions the modal asks.
   */
  readonly messageCount = input<number | null>(null);
  /** Selected uids that already went out successfully, and would be sent again. */
  readonly resendUids = input<readonly string[]>([]);
  /** For a `view`-kind channel, which has no uids to list. */
  readonly lastSentAt = input<Date | string | null>(null);

  readonly confirmed = output<void>();
  readonly cancelled = output<void>();

  protected readonly ModalSize = ModalSize;

  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  protected readonly isStepped = computed(() => this.messageCount() !== null);

  protected readonly hasResend = computed(
    () => this.resendUids().length > 0 || !isNil(this.lastSentAt())
  );

  /**
   * GedMouv is the one channel where a re-send cannot be undone by anyone: `postTour` is not
   * idempotent and GBE has no cancel, so a second send leaves a duplicate tour that someone
   * must delete by hand over there.
   */
  protected readonly isGedmouv = computed(
    () => this.channel() === DeployChannelType.GEDMOUV
  );

  protected readonly config = computed<ModalConfig>(() => {
    this.currentLang();
    const cancel: ModalAction = {
      label: this.translate.instant('common.cancel'),
      callback: () => this.cancelled.emit(),
      color: 'cancel'
    };
    const confirm: ModalAction = {
      label: this.translate.instant('features.plan.deploy.confirm.confirm'),
      callback: () => this.confirmed.emit(),
      // Named after the consequence rather than the control: a re-send is the destructive
      // case and should not wear the same button as a first send.
      color: this.hasResend() ? 'danger' : 'primary'
    };

    return {
      icon: 'warning',
      type: this.hasResend() ? ModalType.WARNING : ModalType.INFO,
      dismissible: true,
      closeOnBackdropClick: true,
      askingModal: true,
      actions: [cancel, confirm]
    };
  });

  protected readonly title = computed(() => {
    this.currentLang();
    return this.translate.instant('features.plan.deploy.confirm.title', {
      name: this.channelName()
    });
  });
}
